// FR-M3c / SD41: maps `NotetakerBot.ui_status` (`notetaker::status::ui_status` server-side —
// `joining|waiting_admission|recording|ended|failed:<reason>`) to the Badge tone/icon/label the
// Meetings page and the ops jobs page both use. One place for this so the two pages can't drift.
import type { BadgeProps } from "@grackle/ui";

const FAILURE_REASON_LABEL: Record<string, string> = {
  needs_signed_in_bot: "needs a signed-in Google account",
  lobby_timeout: "nobody let it in",
  web_join_disabled: "host disabled browser join",
  admission_timeout: "nobody let it in",
  admission_denied: "the host denied its request to join",
  kicked: "removed from the meeting",
  stopped_by_participant: "stopped by a participant",
  crashed: "crashed",
  max_duration: "hit the recording time limit",
  bot_disabled: "the notetaker bot is turned off for this org",
  consent_required: "a consent prompt is required first",
  platform_disabled: "this platform is turned off",
  internal: "an internal error",
  unclaimed: "no notetaker runner picked it up",
  noone_joined: "nobody else joined",
  silence_timeout: "no one spoke for a long time",
};

/** Reasons that stop a recording that was already running (SD56's auto-leave rules among
 * them) — "Couldn't join" would be wrong for a bot that did join and recorded. The recording
 * up to the stop still uploads. */
const STOPPED_REASONS = new Set([
  "kicked",
  "stopped_by_participant",
  "max_duration",
  "noone_joined",
  "silence_timeout",
]);

/** Human label for a `ui_status` value. Covers the pre-SD41 statuses too (`queued`,
 * `in_meeting`, `leaving`, `left`, `removed`) since old rows and the synchronous stub leave/
 * remove flow can still produce them. */
export function botStatusLabel(uiStatus: string): string {
  if (uiStatus.startsWith("failed:")) {
    const reason = uiStatus.slice("failed:".length);
    const prefix = STOPPED_REASONS.has(reason) ? "Stopped" : "Couldn't join";
    return `${prefix} — ${FAILURE_REASON_LABEL[reason] ?? reason}`;
  }
  switch (uiStatus) {
    case "queued":
      return "Queued";
    case "joining":
      return "Joining…";
    case "waiting_admission":
      return "Waiting to be let in";
    case "in_meeting":
    case "recording":
      return "Recording";
    case "leaving":
      return "Leaving…";
    case "ended":
      return "Finished";
    case "left":
    case "removed":
      return "Removed";
    default:
      return uiStatus;
  }
}

export function botStatusTone(uiStatus: string): BadgeProps["tone"] {
  if (uiStatus.startsWith("failed:")) return "outline";
  switch (uiStatus) {
    case "queued":
    case "joining":
      return "accent";
    case "waiting_admission":
      return "now";
    case "in_meeting":
    case "recording":
      return "record";
    case "ended":
      return "success";
    default:
      return "neutral";
  }
}

export function botStatusIcon(uiStatus: string): string {
  if (uiStatus.startsWith("failed:")) return "warning-circle";
  switch (uiStatus) {
    case "in_meeting":
    case "recording":
      return "record";
    case "ended":
      return "check-circle";
    default:
      return "robot";
  }
}

/** The bot is actually in the meeting and capturing — the only state where the Meetings page may
 * say it "is recording". Queued/joining/lobby bots are not in the meeting yet. */
export function isRecordingBotStatus(uiStatus: string): boolean {
  return uiStatus === "recording" || uiStatus === "in_meeting";
}

/** What to tell the user about a bot that is active but not yet recording, or `null` when it is
 * recording (the consent banner applies instead). */
/** SD54: a headless bot still `queued` this long after it was requested means no runner is
 * claiming jobs (pods down, or the bot service token missing). Shown well before the server's
 * 10-minute `failed:unclaimed` sweep (`UNCLAIMED_TIMEOUT_SECS`). */
export const BOT_UNCLAIMED_WARN_SECS = 60;

/** Seconds since `startedAt` (ISO), or 0 when it can't be parsed. */
export function secondsSince(startedAt: string | null | undefined, now: number = Date.now()): number {
  const t = startedAt ? Date.parse(startedAt) : NaN;
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 1000)) : 0;
}

export function botPendingMessage(
  uiStatus: string,
  displayName: string,
  queuedSecs = 0,
): string | null {
  switch (uiStatus) {
    case "queued":
      if (queuedSecs >= BOT_UNCLAIMED_WARN_SECS) {
        return `${displayName} still hasn't been picked up after a minute — the notetaker bot service may be down or not configured. It will be marked as failed if nothing claims it within 10 minutes.`;
      }
      return `${displayName} hasn't joined yet — waiting for a notetaker runner to pick this up.`;
    case "joining":
      return `${displayName} is joining the meeting.`;
    case "waiting_admission":
      return `${displayName} is waiting in the lobby — someone in the meeting needs to let it in.`;
    case "leaving":
      return `${displayName} is leaving the meeting.`;
    default:
      return isRecordingBotStatus(uiStatus) ? null : `${displayName} is not in the meeting.`;
  }
}

/** Still an active, in-progress bot (the Meetings page's "Notetaker in a meeting" section) —
 * everything that isn't a terminal `ui_status`. */
export function isActiveBotStatus(uiStatus: string): boolean {
  return !uiStatus.startsWith("failed:") && !["ended", "left", "removed"].includes(uiStatus);
}
